import { 
  getDatabase, 
  ref, 
  set, 
  update, 
  get 
} from 'firebase/database';
import { MenuItemWithTranslations, Category } from '../types/menu';
import { optimizeAndUploadImage, deleteImage } from './imageService';
import { trackMenuItemInteraction } from './analyticsService';

// ---------------------------------------------------------------------------
// Session cache for the menu tree. Legacy menu images are base64 data URLs,
// so a cold `menu` fetch can be hundreds of KB; caching it (30 s TTL,
// single-flight) makes repeat visits / tab switches feel instant and stops
// concurrent callers from stampeding the database. Any write invalidates it.
// ---------------------------------------------------------------------------
interface MenuCache {
  items: MenuItemWithTranslations[];
  fetchedAt: number;
}

const MENU_CACHE_TTL_MS = 30_000;
let menuCache: MenuCache | null = null;
let menuFetchInFlight: Promise<MenuItemWithTranslations[]> | null = null;

const invalidateMenuCache = (): void => {
  menuCache = null;
};

/**
 * Firebase's `set()` rejects any payload containing `undefined` values
 * ("value argument contains undefined in property ..."). Form state can
 * carry optional fields still set to `undefined` (e.g. `subcategory` on
 * non-pizza items), so payloads are normalized before writing: `undefined`
 * keys are dropped, everything else is kept as-is via a JSON-safe round-trip.
 */
const normalizeForDb = <T>(value: T): T =>
  JSON.parse(JSON.stringify(value)) as T;

export class MenuService {
  static async getMenuItems(): Promise<MenuItemWithTranslations[]> {
    if (menuCache && Date.now() - menuCache.fetchedAt < MENU_CACHE_TTL_MS) {
      return menuCache.items;
    }
    if (menuFetchInFlight) {
      return menuFetchInFlight;
    }

    menuFetchInFlight = (async () => {
      try {
        const db = getDatabase();
        const menuRef = ref(db, 'menu');
        const snapshot = await get(menuRef);

        const items = snapshot.exists()
          ? (Object.values(snapshot.val()) as MenuItemWithTranslations[]).sort(
              (a, b) => (a.order ?? 0) - (b.order ?? 0)
            )
          : [];

        menuCache = { items, fetchedAt: Date.now() };
        return items;
      } catch (error) {
        // Keep the UX (empty menu), but never fail silently.
        console.error('Error fetching menu items:', error);
        return [];
      } finally {
        menuFetchInFlight = null;
      }
    })();

    return menuFetchInFlight;
  }

  static async saveMenuItem(item: MenuItemWithTranslations, imageFile?: File): Promise<void> {
    try {
      const db = getDatabase();
      const menuRef = ref(db, `menu/${item.id}`);

      if (imageFile) {
        const result = await optimizeAndUploadImage(imageFile);
        item.image = result.url;
      }

      // Save the menu item first
      await set(menuRef, normalizeForDb(item));
      invalidateMenuCache();

      // Track the interaction only if save was successful
      try {
        await trackMenuItemInteraction({ item, action: 'edit' });
      } catch (error) {
        // Log but don't throw analytics errors
        console.warn('Analytics tracking failed:', error);
      }
    } catch (error) {
      console.error('Error saving menu item:', error);
      throw error; // Rethrow the main operation error
    }
  }

  static async deleteMenuItem(id: string, imageUrl?: string): Promise<void> {
    const db = getDatabase();
    const menuRef = ref(db, `menu/${id}`);
    
    // Deleting the menu item is the primary operation; an image-cleanup
    // failure must not leave a zombie menu item behind.
    await set(menuRef, null);
    invalidateMenuCache();

    if (imageUrl) {
      try {
        await deleteImage(imageUrl);
      } catch (error) {
        console.warn('Menu item deleted, but its image cleanup failed:', error);
      }
    }
  }

  /**
   * Reorders by writing ONLY each item's `order` child via a multi-path
   * update. The previous implementation rewrote the entire `menu` node on
   * every drag (pushing every item's base64 image back to the database) and
   * could clobber a concurrent save.
   */
  static async updateItemOrder(items: MenuItemWithTranslations[]): Promise<void> {
    const db = getDatabase();
    const updates: Record<string, number> = {};
    items.forEach((item, index) => {
      updates[`menu/${item.id}/order`] = index;
    });
    await update(ref(db), updates);
    invalidateMenuCache();
  }

  static async getCategories(): Promise<Category[]> {
    const db = getDatabase();
    const categoriesRef = ref(db, 'categories');
    const snapshot = await get(categoriesRef);
    
    if (!snapshot.exists()) return [];
    
    return Object.values(snapshot.val());
  }

  static async saveCategory(category: Category): Promise<void> {
    const db = getDatabase();
    const categoryRef = ref(db, `categories/${category.id}`);
    await set(categoryRef, normalizeForDb(category));
  }

  static async deleteCategory(id: string): Promise<void> {
    const db = getDatabase();
    const categoryRef = ref(db, `categories/${id}`);
    await set(categoryRef, null);
  }

  static createEmptyItem(): MenuItemWithTranslations {
    return {
      id: `item_${Date.now()}`,
      price: 0,
      category: '',
      order: 0,
      icon: "🍽️",
      translations: {
        fr: { name: '', description: '' },
        ar: { name: '', description: '' },
        en: { name: '', description: '' }
      },
      addOns: []
    };
  }
} 